import { useEffect, useState } from 'react';
import { accountsClient, isAccountsRouteConfigured } from '../lib/trpc/accounts';
import { getAuthToken } from '../utils/auth';
import { getMerchantRecordId } from '../utils/merchantIdentity';

// Status of the gate/page's own merchant fetch. Callers that only need the
// side effect (the marketing pages) ignore the return value; callers that must
// distinguish "still loading" from "the fetch failed" (EntitlementGate) read it.
export type MerchantHydrationStatus = 'idle' | 'loading' | 'ready' | 'error';

type MerchantHydrationOptions = {
  enabled?: boolean;
  scopeKey?: string | null;
  context?: string;
  refreshKey?: number;
};

// Fetch the base merchant record on mount and hand it to App's
// `setMerchant`. Mirrors the per-page `grabBaseMerchant()` pattern that
// every legacy page (Payouts, Donors, FormWorkspace, etc.) implements
// inline.
//
// App owns initial organization hydration. Legacy pages also use this hook for
// their own refreshes; cancellation and a finite deadline apply to both paths.
export function useMerchantHydration(
  setMerchant: ((data: unknown) => void) | undefined,
  {
    enabled = true,
    scopeKey = null,
    context = 'dashboard merchant hydration',
    refreshKey = 0,
  }: MerchantHydrationOptions = {},
): MerchantHydrationStatus {
  const [progress, setProgress] = useState({
    scopeKey,
    refreshKey,
    status: 'idle' as MerchantHydrationStatus,
  });
  useEffect(() => {
    const setStatus = (status: MerchantHydrationStatus) =>
      setProgress({ scopeKey, refreshKey, status });
    // Guard for the case where the parent didn't thread setMerchant
    // through (e.g. Storybook). No-op if so — the merchant prop will
    // simply stay null and the caller decides how to handle that.
    if (!setMerchant || !enabled) {
      setStatus('idle');
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const deadline = setTimeout(() => controller.abort(), 8000);
    const load = async () => {
      if (!isAccountsRouteConfigured('GET /merchant')) {
        // A URL-less environment cannot hydrate here. Surface a terminal error so gates fall
        // through to the page rather than leaving the user on a permanent loading state.
        setStatus('error');
        clearTimeout(deadline);
        return;
      }
      setStatus('loading');
      const aborted = new Promise<never>((_resolve, reject) => {
        controller.signal.addEventListener(
          'abort',
          () => reject(new Error('Organization loading timed out')),
          { once: true },
        );
      });
      const request = async () => {
        const token = await getAuthToken();
        if (controller.signal.aborted) throw new Error('Organization request cancelled');
        return accountsClient.requestJson('GET /merchant', {
          authorization: token,
          input: {},
          signal: controller.signal,
        }, {
          context,
          retries: 2,
          retryDelay: 1000,
        });
      };
      try {
        const result = await Promise.race([request(), aborted]);
        if (cancelled) return;
        const data: Record<string, unknown> | null = result.data;
        const matchesScope =
          !scopeKey || !data?.clerkOrganizationId || data.clerkOrganizationId === scopeKey;
        if (
          result.ok &&
          getMerchantRecordId(data) &&
          matchesScope &&
          !data?.__charitystackRouterSeed
        ) {
          setMerchant(result.data);
          setStatus('ready');
        } else {
          // Real failure after retries. Callers that render a loading state
          // INSTEAD of the page (EntitlementGate) read this to fall back to the
          // page so it can run its own error/recovery UI — otherwise the user
          // would be stuck on a spinner forever. Marketing pages ignore it and
          // keep their own loading state, exactly as before (behavior preserved).
          setStatus('error');
        }
      } catch (error) {
        if (!cancelled) {
          console.error(`[merchant] ${context} failed`, error);
          setStatus('error');
        }
      } finally {
        clearTimeout(deadline);
      }
    };

    void load();
    return () => {
      cancelled = true;
      clearTimeout(deadline);
      controller.abort();
    };
  }, [context, enabled, scopeKey, refreshKey, setMerchant]);
  if (!enabled || !setMerchant) return 'idle';
  return progress.scopeKey === scopeKey && progress.refreshKey === refreshKey
    ? progress.status
    : 'loading';
}
